import Chip from "@mui/material/Chip";
import Guid from "common/values/guid/guid";
import { JSX } from "react";
import { AccessLevel, Resource, ResourceType } from "./general";

export enum LegalEntitiesPlatformAccessType {
  'OfficerInvitations'
}
export enum LegalEntityAccessType {
  'Member Information',
  'Member Invitations',
  'Admin Invitations',
  'Signing Authority',
  'Member Permissions'
}
export interface IEntityAgreementAccessResource {
  rawText?: string;
  entityAgreementId?: string;
  accessLevel?: string;
  isDisabled?: boolean;
}
export interface ILegalEntitiesPlatformAccessResource {
  rawText?: string;
  legalEntitiesPlatformAccess?: string;
  isDisabled?: boolean;
}
export interface ILegalEntityAccessResource {
  rawText?: string;
  legalEntityId?: string;
  accessType?: string;
  accessLevel?: string;
  isDisabled?: boolean;
}

export class LegalEntitiesPlatformAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.LegalEntitiesPlatformAccess;
  public accessType?: LegalEntitiesPlatformAccessType;
  public rawText?: string;

  public get metaText(): string {
    return `Legal Entities Platform (${this.accessType})`;
  }

  public asJSXElement(): JSX.Element {
    return <div>
      <h3>Legal Entities Platform</h3>
      <p>Access Level: {this.accessType}</p>
    </div>;
  }

  public override toString(): string {
    return `Legal Entities Platform (${this.accessType})`;
  }

  public fromObject(obj: ILegalEntitiesPlatformAccessResource): Resource {
    const resource = new LegalEntitiesPlatformAccess();
    resource.rawText = obj.rawText || "";
    resource.accessType = LegalEntitiesPlatformAccessType[
      obj.legalEntitiesPlatformAccess as keyof typeof LegalEntitiesPlatformAccessType];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      accessType: typeof this.accessType === 'string' ? LegalEntitiesPlatformAccessType[this.accessType] : this.accessType,
      isDisabled: this.isDisabled
    });
  }
}

export class EntityAgreementAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.EntityAgreementAccess;
  public entityAgreementId?: Guid;
  public accessLevel?: AccessLevel;
  public rawText?: string;

  public get metaText(): string {
    return `Entity Agreement ${this.entityAgreementId?.toString()} (${this.accessLevel})`;
  }

  public asJSXElement(): JSX.Element {
    return (
      <Chip 
        label={
          <>
            <strong>Entity Agreement</strong>:&nbsp;<span>{this.entityAgreementId?.toString()}</span>&nbsp;<span>({this.accessLevel})</span>
          </>
        } />
    );
  }

  public override toString(): string {
    return `Entity Agreement: ${this.entityAgreementId?.toString()} (${this.accessLevel})`;
  }

  public fromObject(obj: IEntityAgreementAccessResource): Resource {
    const resource = new EntityAgreementAccess();
    resource.rawText = obj.rawText || "";
    resource.entityAgreementId = obj.entityAgreementId ? new Guid(obj.entityAgreementId) : undefined;
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      entityAgreementId: this.entityAgreementId?.toString(),
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      isDisabled: this.isDisabled
    });
  }
}

export class LegalEntityAccess extends Resource {
  public readonly resourceType: ResourceType = ResourceType.LegalEntityAccess;
  public legalEntityId?: Guid;
  public accessType?: LegalEntityAccessType;
  public accessLevel?: AccessLevel;
  public rawText?: string;

  public get metaText(): string {
    return `Legal Entity ${this.legalEntityId?.toString()} (${this.accessType}, ${this.accessLevel})`;
  }

  public asJSXElement(): JSX.Element {
    return (
      <Chip 
        label={
          <>
            <strong>{this.accessType}</strong>:&nbsp;<span>({this.accessLevel})</span>
          </>
        } />
    );
  }

  public override toString(): string {
    return `${this.accessType}: ${this.accessLevel}`;
  }

  public fromObject(obj: ILegalEntityAccessResource): Resource {
    const resource = new LegalEntityAccess();
    resource.rawText = obj.rawText || "";
    resource.legalEntityId = obj.legalEntityId ? Guid.fromJSON(obj.legalEntityId) : undefined;
    resource.accessType = LegalEntityAccessType[obj.accessType as keyof typeof LegalEntityAccessType];
    resource.accessLevel = AccessLevel[obj.accessLevel as keyof typeof AccessLevel];
    resource.isDisabled = !!obj.isDisabled;
    return resource;
  }

  public toJSONString(): string {
    return JSON.stringify({
      resourceType: ResourceType[this.resourceType],
      legalEntityId: this.legalEntityId?.toString(),
      accessType: typeof this.accessType === 'string' ? LegalEntityAccessType[this.accessType] : this.accessType,
      accessLevel: typeof this.accessLevel === 'string' ? AccessLevel[this.accessLevel] : this.accessLevel,
      isDisabled: this.isDisabled
    });
  }

  public static getPermutations(): LegalEntityAccess[] {
    const fieldIterations: [key: string, value: string[]][] = [
      ['accessType', Object.keys(LegalEntityAccessType).filter((key) => Number.isNaN(Number(key)))],
      ['accessLevel', Object.keys(AccessLevel).filter((key) => Number.isNaN(Number(key)))],
    ];
    return this.generatePermutations(LegalEntityAccess, fieldIterations) as LegalEntityAccess[];
  }
}
